import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { useAppContext } from '../../../../Services/AppContext';
import { HealthPlanPricingFixPrice } from '../../../../Enums/HealthPlanPricing.enum';
import Price from '../../../../Components/Price/Price';
import useCurrency from '../../../Region/Hooks/useCurrency';
import { HealthPlanPricingSetup } from '../../healthPlanPricing';
import { Country } from '../../../../Enums/Country.enum';
import { HealthPlanCode } from '../../../../Enums/HealthPlanCode.enum';

interface HealthPlanPriceResultProps {
	telemedicinePriceFormKey: string;
	presentPriceFormKey: string;
	priceForSupportActivitiesPlanKey: string;
	type: ResultType;
	healthPlanData: object;
	showCalculation?: boolean;
	saleInPercent?: number;
	healthPlanCode: HealthPlanCode;
}

export enum ResultType {
	PRESENT = 0,
	TELEMEDICINE = 1,
	FIX = 2,
	TOTAL = 3,
	TOTAL_SALE = 4,
	CUSTOM_FOR_ACTIVITIES = 5,
}

const HealthPlanPriceResult: React.FC<HealthPlanPriceResultProps> = props => {
	const {
		telemedicinePriceFormKey,
		presentPriceFormKey,
		priceForSupportActivitiesPlanKey,
		healthPlanData,
		type,
		showCalculation = false,
		saleInPercent,
		healthPlanCode,
	} = props;
	const { currency, globalTemplate } = useAppContext();
	const country: Country = globalTemplate as Country;
	const { currencyCode } = useCurrency();

	const { watch } = useFormContext();
	const telemedicinePriceValue = watch(telemedicinePriceFormKey);
	const presentPriceValue = watch(presentPriceFormKey);
	const priceForSupportActivitiesPlanValue = watch(
		priceForSupportActivitiesPlanKey,
	);
	const { present, telemedicine } = getAppointmentsCount(healthPlanData);

	let resultPrice = 0;
	let priceFactor = 0;
	let factor = 0;

	const fixPrice =
		HealthPlanPricingSetup[country][healthPlanCode].fixedPriceForHealthPlan;

	switch (type) {
		case ResultType.PRESENT:
			factor = present;
			priceFactor = presentPriceValue || 0;
			resultPrice = (presentPriceValue || 0) * present;
			break;
		case ResultType.TELEMEDICINE:
			factor = telemedicine;
			priceFactor = telemedicinePriceValue || 0;
			resultPrice = (telemedicinePriceValue || 0) * telemedicine;
			break;
		case ResultType.FIX:
			resultPrice = fixPrice;
			break;
		case ResultType.TOTAL:
			resultPrice = fixPrice;
			resultPrice += (presentPriceValue || 0) * present;
			resultPrice += (telemedicinePriceValue || 0) * telemedicine;
			resultPrice += parseFloat(priceForSupportActivitiesPlanValue);
			resultPrice = saleInPercent
				? resultPrice - (resultPrice / 100) * saleInPercent
				: resultPrice;
			break;
		case ResultType.TOTAL_SALE:
			let totalPrice = fixPrice;
			totalPrice += (presentPriceValue || 0) * present;
			totalPrice += (telemedicinePriceValue || 0) * telemedicine;
			totalPrice += parseFloat(priceForSupportActivitiesPlanValue);
			resultPrice = totalPrice - (totalPrice / 100) * 10;
			break;
		case ResultType.CUSTOM_FOR_ACTIVITIES:
			resultPrice = parseFloat(priceForSupportActivitiesPlanValue);
			break;
	}

	if (showCalculation) {
		return (
			<>
				{factor} x <Price price={priceFactor} customSuffix={currencyCode} />
			</>
		);
	}

	return <Price price={resultPrice} customSuffix={currencyCode} />;
};

export const getAppointmentsCount = healthPlanData => {
	let present = 0;
	let telemedicine = 0;

	if (healthPlanData) {
		for (const month of Object.keys(healthPlanData)) {
			const monthData = healthPlanData[month].children || [];
			for (const monthItem of monthData) {
				if (monthItem.type === 'appointment') {
					const types = [
						monthItem.extra?.data?.consultationType ?? null,
						monthItem.extra?.data?.meetingType?.value,
						monthItem.extra?.data?.meetingType,
					];
					if (types.indexOf('telemedicine') !== -1) {
						telemedicine++;
					} else {
						present++;
					}
				}
			}
		}
	}

	return { present, telemedicine };
};

export default HealthPlanPriceResult;
